/**
 * French (Canada) locale for phone-numbers page.
 * Export is the "phoneNumbers" subtree for host i18n merge.
 */
import type { PhoneNumbersLocale } from './en'

export const phoneNumbersFrCa = {
  list: {
    title: `Numéros de téléphone`,
    serverGroup: `Groupe de serveurs :`,
    searchPlaceholder: `Rechercher...`,
    loading: `Chargement des numéros de téléphone...`,
    error: `Échec du chargement des numéros de téléphone`,
    empty: `Aucun numéro de téléphone trouvé pour ce PBX`,
    noResults: `Aucun numéro de téléphone ne correspond à votre recherche`,
    columnName: `Nom`,
    columnNumber: `Numéro`,
    columnExtension: `Poste`,
    columnCallerId: `ID de l'appelant`,
    columnType: `Type`,
    columnTollFree: `Sans frais`,
    columnSms: `SMS`,
    columnE911: `e911`,
    columnVoiceProvider: `Fournisseur vocal`,
    columnSmsProvider: `Fournisseur SMS`,
    columnProviders: `Fournisseurs`,
    columnStatus: `Statut`,
    columnUpdated: `Mis à jour`,
    yes: `Oui`,
    no: `Non`,
    smsActive: `Actif`,
    e911Verified: `Vérifié`,
    createButton: `+ Créer un numéro de téléphone`,
    createDisabledTooltip: `La création de numéros est gérée par l'équipe téléphonie`,
    filterButton: `Filtres`,
    copyNumber: `Copier le numéro`,
    toast: {
      createSuccess: `Numéro de téléphone créé`,
      createFailed: `Échec de la création du numéro de téléphone`,
      copied: `Numéro copié dans le presse-papiers`,
      copyFailed: `Échec de la copie du numéro`,
    },
  },
  filters: {
    status: `Statut`,
    allStatuses: `Tous`,
    type: `Type`,
    allTypes: `Tous`,
    voiceProvider: `Fournisseur vocal`,
    smsProvider: `Fournisseur SMS`,
    allProviders: `Tous`,
    tollFree: `Sans frais`,
    any: `Tous`,
    showDeleted: `Afficher les supprimés`,
    clearAll: `Effacer les filtres`,
  },
  types: {
    callerId: `Identification de l'appelant`,
    smsOnly: `SMS seulement`,
    standard: `Standard`,
    temporary: `Temporaire`,
  },
  bulk: {
    title: `Mise à jour en bloc du statut`,
    description: `Mettre à jour le statut des numéros de téléphone sélectionnés.`,
    selected: `{{count}} sélectionné(s)`,
    apply: `Appliquer`,
    updateStatus: `Mettre à jour le statut`,
    clearSelection: `Effacer`,
    maxExceeded: `Maximum 25 à la fois`,
    selectAll: `Sélectionner tous les numéros de téléphone`,
    selectRow: `Sélectionner {{number}}`,
    toast: {
      success: `Statut mis à jour avec succès`,
      failed: `Échec de la mise à jour en bloc du statut`,
    },
    results: {
      titleSuccess: `Mise à jour en bloc terminée`,
      titleAllFailed: `Échec de la mise à jour en bloc`,
      titlePartial: `Mise à jour en bloc partiellement terminée`,
      summarySuccess: `{{count}} numéro(s) de téléphone mis à jour avec succès.`,
      summaryAllFailed: `Les {{count}} numéro(s) de téléphone n'ont pas pu être mis à jour.`,
      summaryPartial: `{{successCount}} sur {{totalCount}} mis à jour. {{failedCount}} échoué(s) :`,
      colNumber: `Numéro`,
      colStatus: `Statut actuel`,
      colError: `Erreur`,
      close: `Fermer`,
    },
  },
  detail: {
    loading: `Chargement du numéro de téléphone...`,
    error: `Échec du chargement du numéro de téléphone`,
    backToList: `Retour à la liste`,
    editButton: `Modifier le numéro de téléphone`,
    deleteTitle: `Supprimer le numéro de téléphone`,
    deleteButton: `Supprimer`,
    deleteConfirmation: `Ceci supprimera temporairement le numéro de téléphone. Il pourra être restauré ultérieurement si nécessaire. Êtes-vous sûr ?`,
    restoreButton: `Restaurer`,
    noopSaveButton: `Re-sauvegarder`,
    deleted: `Supprimé`,
    sectionGeneral: `Informations générales`,
    sectionProviders: `Fournisseurs`,
    sectionRouting: `Routage`,
    sectionSms: `SMS`,
    sectionE911: `Informations E911`,
    sectionTimestamps: `Horodatages`,
    sectionSmsDiagnostics: `Diagnostics SMS Telnyx`,
    diagnosticWarning: `Avertissement`,
    fieldName: `Nom`,
    fieldNumber: `Numéro`,
    fieldPbx: `Central`,
    fieldType: `Type`,
    fieldCallerId: `ID de l'appelant`,
    fieldExtension: `Poste`,
    fieldTollFree: `Sans frais`,
    fieldCountryCode: `Code de pays`,
    fieldLocalPresence: `Présence locale`,
    fieldCampaignId: `ID de campagne`,
    fieldVoiceProvider: `Fournisseur vocal`,
    fieldSmsProvider: `Fournisseur SMS`,
    fieldSmsEnabled: `SMS activé`,
    fieldSmsStatus: `Statut SMS`,
    fieldSmsRelay: `Relais SMS`,
    fieldSmsRelayIntegration: `Intégration de relais SMS`,
    fieldSmsGroup: `Groupe SMS`,
    fieldE911Status: `Statut E911`,
    fieldE911Address: `Adresse`,
    fieldE911Address2: `Adresse 2`,
    fieldE911City: `Ville`,
    fieldE911State: `État`,
    fieldSendFaxesFrom: `Envoyer des fax depuis`,
    usePbxDefault: `Utiliser la valeur par défaut du PBX`,
    fieldPbxServerGroup: `Groupe de serveurs PBX`,
    fieldSourceTracking: `Suivi de source`,
    fieldStatus: `Statut`,
    fieldE911PostalCode: `Code postal`,
    fieldCreatedAt: `Créé`,
    fieldUpdatedAt: `Mis à jour`,
    viewInProvider: `Voir dans {{provider}}`,
    sectionMetrics: `Métriques`,
    fieldCallStats: `Statistiques d'appels`,
    callStatsLink: `Statistiques d'appels`,
    on: `Activé`,
    off: `Désactivé`,
    registered: `Enregistré`,
    inactive: `Inactif`,
    notRegistered: `Non enregistré`,
    toast: {
      updateSuccess: `Numéro de téléphone mis à jour`,
      updateFailed: `Échec de la mise à jour du numéro`,
      deleteSuccess: `Numéro de téléphone supprimé`,
      deleteFailed: `Échec de la suppression du numéro de téléphone`,
      restoreSuccess: `Numéro de téléphone restauré`,
      restoreFailed: `Échec de la restauration du numéro de téléphone`,
      noopSaveSuccess: `Effets secondaires redéclenchés`,
      noopSaveFailed: `Échec de la re-sauvegarde`,
    },
  },
  edit: {
    title: `Modifier le numéro de téléphone`,
    numberLabel: `Numéro`,
    nameLabel: `Nom`,
    namePlaceholder: `Nom du numéro de téléphone`,
    typeLabel: `Type`,
    statusLabel: `Statut`,
    selectStatus: `— Sélectionner le statut —`,
    voiceProviderLabel: `Fournisseur vocal`,
    smsProviderLabel: `Fournisseur SMS`,
    selectProvider: `— Sélectionner —`,
    unassigned: `Non attribué`,
    callerIdLabel: `ID de l'appelant`,
    sendFaxesFromLabel: `Envoyer des fax depuis`,
    usePbxDefault: `Utiliser la valeur par défaut du PBX`,
    extensionLabel: `Poste`,
    extensionPlaceholder: `UUID du poste`,
    extensionHelp: `Poste optionnel pour le numéro de téléphone.`,
    noExtension: `Aucun`,
    sourceTrackingLabel: `Suivi de source`,
    sourceTrackingHelp: `Ajouter jusqu'à 6 caractères devant l'ID de l'appelant pour les appels entrants. Ceci est couramment utilisé pour suivre les campagnes publicitaires.`,
    localPresenceLabel: `Présence locale`,
    localPresenceHelp: `Les groupes de présence locale créent un ensemble de numéros à partir desquels un poste de ligne avec présence locale activée peut passer un appel afin de paraître local pour le destinataire.`,
    e911Section: `Paramètres d'urgence 911 (E911)`,
    e911AddressLabel: `Adresse`,
    e911Address2Label: `Appartement/suite`,
    e911CityLabel: `Ville`,
    e911StateLabel: `État/Province`,
    e911PostalCodeLabel: `Code postal`,
    e911Disclaimer: `Le E911 est un système utilisé en Amérique du Nord pour fournir automatiquement la localisation de l'appelant aux répartiteurs du 911. Veuillez vous assurer que ces informations sont exactes — la vie de quelqu'un pourrait en dépendre.`,
    smsSection: `Paramètres SMS`,
    smsEnabledLabel: `SMS activé`,
    smsEnabledWarning: `Avertissement : l'activation de la messagerie texte déclenchera une demande de portabilité du eSPID pour ce numéro, ce qui peut causer des interruptions de service pour le client. Veuillez vous assurer que c'est bien l'action souhaitée avant de continuer.`,
    smsRelayLabel: `Relais SMS`,
    smsRelayIntegrationLabel: `Intégration du relais SMS`,
    smsRelayIntegrationDisabledHelp: `Activez le relais SMS pour choisir une intégration.`,
    noIntegration: `Aucune`,
    integrationSearchPlaceholder: `Rechercher des intégrations...`,
    integrationSearchAriaLabel: `Rechercher des intégrations de relais SMS par nom`,
    noIntegrationMatches: `Aucune intégration ne correspond à cette recherche.`,
    campaignIdLabel: `ID de campagne`,
    campaignIdHelp: `Requis pour l'envoi de SMS. Avant de définir cette valeur, assurez-vous qu'un ID de campagne est lié à ce numéro chez le fournisseur concerné. Ne pas le faire peut entraîner des interruptions de service, des échecs de livraison et/ou des amendes.`,
    titleWithNumber: `Modifier le numéro de téléphone — {{number}}`,
    advancedSection: `Avancé`,
    e911NotConfigured: `Non configuré`,
    extensionSearchPlaceholder: `Rechercher des extensions...`,
    extensionSearchAriaLabel: `Rechercher des extensions par nom ou numéro`,
    noExtensionMatches: `Aucune extension ne correspond à cette recherche.`,
    cancel: `Annuler`,
    save: `Enregistrer`,
  },
  create: {
    title: `Créer un numéro de téléphone`,
    numberLabel: `Numéro de téléphone`,
    numberPlaceholder: `+1234567890`,
    numberHelp: `Doit inclure l'indicatif de pays (ex. +18015551234 pour les É.-U., +442079460958 pour le R.-U.).`,
    numberHelpWithPrefix: `Entrez le numéro sans l'indicatif du pays.`,
    selectType: `— Sélectionner le type —`,
    saveButton: `Créer`,
    createMore: `Créer plus`,
    searchStateProvince: `Rechercher...`,
    noStateProvinceMatches: `Aucun résultat`,
  },
  validation: {
    numberRequired: `Le numéro de téléphone est requis`,
    numberInvalid: `Entrez un numéro de téléphone valide (ex. +1 (801) 555-1234)`,
    numberInvalidNational: `Entrez un numéro de téléphone valide (ex. (801) 555-1234)`,
    numberMissingCountryCode: `Le numéro de téléphone doit inclure un indicatif de pays (ex. +1 pour les É.-U., +44 pour le R.-U.)`,
    nameRequired: `Le nom est requis`,
    typeRequired: `Le type est requis`,
    statusRequired: `Le statut est requis`,
    voiceProviderRequired: `Le fournisseur de voix est requis`,
    callerIdTooLong: `Maximum 15 caractères`,
    callerIdInvalidChars: `Seuls les lettres, chiffres et espaces sont autorisés`,
    callerIdAllDigits: `Ne peut pas être composé uniquement de chiffres`,
    campaignIdFormat: `Doit contenir exactement 7 caractères alphanumériques`,
    sourceTrackingFormat: `1 à 6 caractères alphanumériques uniquement`,
    e911Incomplete: `L'adresse, la ville, l'état et le code postal sont tous requis lorsqu'un champ E911 est rempli`,
  },
  pagination: {
    resultsPerPage: `Résultats par page :`,
    previousPage: `Page précédente`,
    nextPage: `Page suivante`,
    jumpToPage: `Aller à la page {{page}}`,
    all: `Tous`,
    recordRange: `{{start}}-{{end}} sur {{total}}`,
    recordRangeEmpty: `0 sur 0`,
  },
} satisfies PhoneNumbersLocale
